/* eslint-disable @typescript-eslint/camelcase */
import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { ClickableTableRow, HtmlTable } from '../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { PDFSafeTableHeaderCaption } from '../../../../CommonComponents/OPL/HtmlTable/components/PDFSafeTableHeaderCaption/PDFSafeTableHeaderCaption';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { ListItem } from '../../../../CommonComponents/OPL/List/ListItem';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '../../../../CommonComponents/OPL/Menu/Menu';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { Toggle } from '../../../../CommonComponents/OPL/Toggle/Toggle';
import { Tooltip } from '../../../../CommonComponents/OPL/Tooltip/Tooltip';
import {
  canEditLinkedAccountOwnerAndSyncSelector,
  hasAccessToEditProfileSelector,
  hasAccessToLinkAccountsSelector,
  hasPlaidSelector,
  isFinancialPlanEnabledSelector,
  selectAkoyaPhase,
} from '../../../../CommonSelectors/CommonAreaSelector';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { PlaidLinkInfoAlert } from '../../../../Plaid/components/PlaidLinkInfoAlert/PlaidLinkInfoAlert';
import { QuovoDisabledMessageModal } from '../../../../Quovo/components/QuovoDisabledMessage/QuovoDisabledMessageModal';
import { WorkflowIdEnum } from '../../../../Workflow/api/useWorkflowDataQuery';
import { usePlaidLink } from '../../../../_hooks/plaid/usePlaidLink';
import { ACCOUNTS_QUERY_KEY } from '../../AccountsConstants';
import { useAccountBeneficiariesMutation } from '../../api/useAccountBeneficiariesMutation';
import { useAccountPortfolioMutation } from '../../api/useAccountPortfolioMutation';
import { useAccountSaveMutation } from '../../api/useAccountSaveMutation';
import { AccountDTO, LOANS_AND_CREDIT_CARDS } from '../../api/useAccountsQuery';
import { MANAGE_LINKS_QUERY_KEY } from '../../api/useLinksQuery';
import { ManageAkoyaLinkedAccounts } from '../../components/ManageAkoyaLinkedAccounts/ManageAkoyaLinkedAccounts';
import { useAccountContext } from '../../context/useAccountContext';
import { TabSlugOptions, WorkflowTabSlugOptions } from '../../types/tab';
import { AccountPreferencesLoading } from './AccountPreferences.loading';
import { SelectAccountToBeDeleted } from './AccountPreferences.meta';
import { AccountForm } from './components/AccountForm/AccountForm';
import {
  accountPortfolioDataToForm,
  accountPortfolioFormToBody,
  ACCOUNTS_WITH_BENEFICIARIES_TAB,
  bankAccountBeneficiariesFormToBeneficiariesBody,
  bankAccountsFormToBody,
  dataToForm,
  getAccountHasAssetAllocationTab,
} from './components/AccountForm/AccountForm.meta';
import { AccountStatusTooltip } from './components/AccountStatusTooltip';
import { AddAccountPreSelectionModal } from './components/AddAccountPreSelectionModal/AddAccountPreSelectionModal';
import { DeleteModal } from './components/DeleteModal/DeleteModal';
import { useAccountPreferences, UseAccountPreferencesResponse } from './useAccountPreferences';
import { canEditAggregatedLinkedAccountDescriptionOnly } from './accountEditAccess';

interface Props {
  clientId: string;
  entityId?: string;
}

export const AccountPreferences = ({ clientId, entityId }: Props) => {
  const { data, isError, isFetching, refetch } = useAccountPreferences();

  if (isFetching) {
    return <AccountPreferencesLoading />;
  }

  if (isError) {
    return (
      <Box
        display="flex"
        width="100%"
        height="80%"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        my="56"
      >
        <ErrorState onAction={() => refetch()} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return <Preferences clientId={clientId} entityId={entityId} data={data} />;
};

interface PreferencesProps {
  clientId: string;
  entityId?: string;
  data: UseAccountPreferencesResponse;
}

const Preferences = ({ clientId, entityId, data }: PreferencesProps) => {
  const [isAddAccountPreSelectionModalOpen, setIsAddAccountPreSelectionModalOpen] = useState(false);

  const [showQuovoMessage, setShowQuovoMessage] = useState(false);
  const [isAkoyaLinkManagementModalOpen, setIsAkoyaLinkManagementModalOpen] = useState(false);

  const [accountSelected, setAccountSelected] = useState<{
    id: string;
    tabSelected?: string;
  } | null>(null);

  const queryClient = useQueryClient();

  const { isWorkflow, workflowId } = useAccountContext();

  const [showHiddenAccounts, setShowHiddenAccounts] = useState(false);
  const { refetch } = useAccountPreferences();

  const [accountToDelete, setAccountToDelete] = useState<SelectAccountToBeDeleted>(null);

  const isOpenModalOnRedirect = useRef(!!entityId);

  const dispatch = useDispatch();

  const hasPlaidAccess = useSelector(hasPlaidSelector);
  const hasAccessToLinkAccounts = useSelector(hasAccessToLinkAccountsSelector);
  const hasAccessToEditProfile = useSelector(hasAccessToEditProfileSelector);
  const canEditLinkedAccountOwnerAndSync = useSelector(canEditLinkedAccountOwnerAndSyncSelector);

  const akoyaPhase = useSelector(selectAkoyaPhase);

  const {
    open: openPlaidModal,
    isLoading: isLoadingPlaidUpdateLink,
    queryError: openPlaidError,
    mutationError: upsertLinkError,
  } = usePlaidLink({
    clientId,
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY, MANAGE_LINKS_QUERY_KEY]);
    },
  });

  const isFinancialPlanEnabled = useSelector(isFinancialPlanEnabledSelector);

  const { mutateAsync: saveBankAccount } = useAccountSaveMutation();
  const { mutateAsync: saveAccountPortfolio } = useAccountPortfolioMutation();
  const { mutateAsync: saveAccountBeneficiaries } = useAccountBeneficiariesMutation();

  const cloneAccount = async (account: AccountDTO) => {
    const clonedFormData = dataToForm(account, data.options);
    const baseName = clonedFormData.AccountName || account.Description;
    const clonedAccountName = `Cloned - ${baseName}`;

    const clonedTotalBalance =
      Number(clonedFormData.NonSpecificPositionsValue ?? 0) +
      Number(clonedFormData.SpecificPositionsValue ?? 0);

    const clonedBankAccountBody = bankAccountsFormToBody({
      ...clonedFormData,
      AccountId: undefined,
      AccountName: clonedAccountName,
      IsOrion: false,
      HasPositions: false,
      Positions: [],
      NonSpecificPositionsValue: clonedTotalBalance,
      SpecificPositionsValue: 0,
    });

    const response = await saveBankAccount(clonedBankAccountBody);

    if (response) {
      // Save account portfolio (asset allocation)
      const defaultAccountPortfolio = accountPortfolioDataToForm({
        accountType: account.AccountType,
        accountOptions: data.options,
      });

      const accountPortfolioBody = accountPortfolioFormToBody(defaultAccountPortfolio ?? null);

      if (
        getAccountHasAssetAllocationTab(account.AccountType, {
          annuityType: account.AnnuityType,
        }) &&
        accountPortfolioBody
      ) {
        await saveAccountPortfolio({
          ...accountPortfolioBody,
          AccountId: response.AccountId,
        });
      }

      // Save beneficiaries for the cloned account
      if (
        ACCOUNTS_WITH_BENEFICIARIES_TAB.includes(account.AccountType) &&
        clonedFormData.AccountBeneficiaries &&
        clonedFormData.AccountBeneficiaries.Beneficiaries.length > 0
      ) {
        await saveAccountBeneficiaries({
          ...bankAccountBeneficiariesFormToBeneficiariesBody(
            clonedFormData.AccountBeneficiaries,
            data.options.BeneficiariesOptions,
          ),
          AccountId: response.AccountId,
        });
      }
    }

    await queryClient.invalidateQueries(ACCOUNTS_QUERY_KEY);
  };

  useEffect(() => {
    if (data && isOpenModalOnRedirect.current && entityId) {
      setAccountSelected({ id: entityId });
    }
  }, [clientId, entityId, dispatch, data]);

  if (isLoadingPlaidUpdateLink) {
    return <AccountPreferencesLoading />;
  }

  if (!data) {
    return null;
  }

  const hasPlaidAccountLinking = hasPlaidAccess && hasAccessToLinkAccounts;

  const hasAkoyaAccountLinking = akoyaPhase !== 'none';

  const showAddAccountButton =
    (data.allowAddManualAccount && hasAccessToEditProfile) ||
    hasPlaidAccountLinking ||
    hasAkoyaAccountLinking;

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt="24">
        <Toggle
          checked={showHiddenAccounts}
          onChange={() => setShowHiddenAccounts(!showHiddenAccounts)}
        >
          Show Hidden Accounts
        </Toggle>

        {showAddAccountButton && (
          <Button
            leftIcon="fas fa-plus"
            onClick={() => {
              setIsAddAccountPreSelectionModalOpen(true);
            }}
          >
            Add Account
          </Button>
        )}
      </Box>

      {(openPlaidError || upsertLinkError) && (
        <Alert
          mt="24"
          message={
            openPlaidError?.toString() ||
            upsertLinkError?.toString() ||
            'Sorry, something went wrong. Please try again.'
          }
          title={openPlaidError?.name || upsertLinkError?.name}
          variant="error"
        />
      )}

      <PlaidLinkInfoAlert mt="24" mb="16" />

      {data && (
        <>
          {Object.entries(data.accounts).map(([key, accounts]) => {
            const isEstatePlanningColumnLayout =
              workflowId === WorkflowIdEnum.EstatePlanning && key !== LOANS_AND_CREDIT_CARDS;

            if (accounts.length === 0) {
              return (
                <HtmlTable mt="24" key={key} variant={isWorkflow ? 'bordered' : undefined}>
                  <Box p="16">
                    <Text fontWeight={600} mb="8">
                      {key}
                    </Text>

                    <Text fontSize="small" color="neutral.600">
                      You have not added any items.
                    </Text>
                  </Box>
                </HtmlTable>
              );
            }

            return (
              <HtmlTable
                mt="24"
                key={key}
                variant={isWorkflow ? 'bordered' : undefined}
                columnSpacing="small"
              >
                <table>
                  <colgroup>
                    <col width={isEstatePlanningColumnLayout ? '10%' : '30%'} />
                    <col width="20%" />
                    <col width={isEstatePlanningColumnLayout ? '20%' : '25%'} />
                    <col width="15%" />
                    {isEstatePlanningColumnLayout && <col width="15%" />}
                    <col width="10%" />
                  </colgroup>

                  <thead>
                    <PDFSafeTableHeaderCaption colSpan={isEstatePlanningColumnLayout ? 6 : 5}>
                      {key}
                    </PDFSafeTableHeaderCaption>
                    <tr>
                      <th>
                        <Text variant="tableHeader" textAlign="left">
                          Name
                        </Text>
                      </th>

                      <th>
                        <Text variant="tableHeader" textAlign="right">
                          Balance
                        </Text>
                      </th>

                      <th>
                        <Text variant="tableHeader" textAlign="left">
                          Owner
                        </Text>
                      </th>

                      <th>
                        {isEstatePlanningColumnLayout ? (
                          <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                            <Text variant="tableHeader" textAlign="right">
                              Liquidity
                            </Text>

                            <Tooltip
                              content="Liquidity refers to the ease with which property can be quickly converted into cash without incurring significant losses."
                              placement="top"
                            >
                              <Icon
                                className="far fa-info-circle"
                                color="neutral.500"
                                fontSize="small"
                                ml="4"
                              />
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                            <Text variant="tableHeader" textAlign="left">
                              Goal
                            </Text>

                            <Tooltip
                              content="Goal/s you will fund with this account."
                              placement="top"
                            >
                              <Icon
                                className="far fa-info-circle"
                                color="neutral.500"
                                fontSize="small"
                                ml="4"
                              />
                            </Tooltip>
                          </Box>
                        )}
                      </th>

                      {isEstatePlanningColumnLayout && (
                        <th>
                          <Text variant="tableHeader" textAlign="center">
                            In Gr. Est.
                          </Text>
                        </th>
                      )}

                      <th>
                        <Text variant="tableHeader">Status</Text>
                      </th>

                      <th aria-label={`Actions for ${key}`} />
                    </tr>
                  </thead>

                  <tbody>
                    {accounts.map(account => {
                      if (!account.ShowAccount && !showHiddenAccounts) {
                        return null;
                      }

                      return (
                        <ClickableTableRow
                          variant="clickable"
                          key={account.AccountId}
                          onClick={() => {
                            setAccountSelected({ id: account.AccountId });
                          }}
                        >
                          <td>
                            <Tooltip
                              content={account.Description || account.AccountName}
                              placement="top-start"
                              isOverflowWrap
                            >
                              <Box display="flex" flexDirection="column">
                                <Text fontWeight={500} color="neutral.700" isTruncated>
                                  {account.Description || account.AccountName}
                                </Text>
                                <Text color="neutral.500" fontSize="small">
                                  {account[data.dynamicKey]}
                                </Text>
                              </Box>
                            </Tooltip>
                          </td>

                          <td>
                            <Text
                              fontWeight={500}
                              color="neutral.700"
                              textAlign="right"
                              width="100%"
                            >
                              {formatCurrency(
                                account.NonSpecificPositionsValue + account.SpecificPositionsValue,
                              )}
                            </Text>
                          </td>

                          <td>
                            <Tooltip content={account.AccountOwner} placement="top-start">
                              <Text fontWeight={500} color="neutral.700" isTruncated>
                                {account.AccountOwner}
                              </Text>
                            </Tooltip>
                          </td>

                          <td>
                            {isEstatePlanningColumnLayout ? (
                              <Box>
                                <Text>{account.IsLiquid ? 'Liquid' : 'Illiquid'}</Text>
                              </Box>
                            ) : (
                              <Box>
                                {account.AccountEarmarkIcons?.map(({ icon, tooltip }, index) => (
                                  <Tooltip
                                    content={tooltip}
                                    placement="bottom"
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${index}-${icon}`}
                                  >
                                    <Icon
                                      className={icon}
                                      color="neutral.500"
                                      fontSize="small"
                                      pr={4}
                                      pl={4}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            )}
                          </td>

                          {isEstatePlanningColumnLayout && (
                            <td>
                              <Box display="flex" justifyContent="center">
                                {account.IncludeInGrossEstate ? (
                                  <Icon className="fas fa-check" color="success.500" />
                                ) : (
                                  <Icon className="fas fa-times-octagon" color="error.500" />
                                )}
                              </Box>
                            </td>
                          )}

                          <td>
                            <Box display="flex" justifyContent="center">
                              {account.IsLinked ||
                              account.IsOrion ||
                              account.IsOrionManagedAccount ? (
                                <>
                                  {account.IsLinkBroken ? (
                                    <Tooltip
                                      placement="left"
                                      isInteractive
                                      content={
                                        <AccountStatusTooltip
                                          title="Account Disconnected"
                                          timeStamp={account.LastUpdatedTimeStamp}
                                          buttonText="Fix Connection"
                                          onClick={ev => {
                                            ev.stopPropagation();
                                            if (hasPlaidAccess) {
                                              openPlaidModal(account.ExternalInstitutionLinkId);
                                            } else {
                                              setShowQuovoMessage(true);
                                            }
                                          }}
                                        />
                                      }
                                    >
                                      <Icon className="far fa-unlink" />
                                    </Tooltip>
                                  ) : (
                                    <Tooltip
                                      placement="left"
                                      content={
                                        <AccountStatusTooltip
                                          title="Account Connected"
                                          timeStamp={account.LastUpdatedTimeStamp}
                                        />
                                      }
                                    >
                                      <Icon className="far fa-link" />
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Tooltip
                                  placement="left"
                                  content={
                                    <AccountStatusTooltip
                                      title="Manual Account"
                                      timeStamp={account.LastUpdatedTimeStamp}
                                    />
                                  }
                                >
                                  <Icon className="far fa-user-edit" />
                                </Tooltip>
                              )}
                            </Box>
                          </td>

                          <td>
                            <Box width="100%" display="flex" justifyContent="flex-end">
                              <Menu>
                                <MenuTrigger>
                                  <IconButton
                                    aria-label="Account menu"
                                    ml="4"
                                    icon="far fa-ellipsis-h"
                                    variant="subtleInactive"
                                    onClick={e => e.stopPropagation()}
                                  />
                                </MenuTrigger>

                                <MenuContent align="end">
                                  {isFinancialPlanEnabled && account.HasAsset && (
                                    <MenuItem
                                      onSelect={() => {
                                        setAccountSelected({
                                          id: account.AccountId,
                                          tabSelected: 'Asset Allocation',
                                        });
                                      }}
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <ListItem icon="far fa-chart-pie">Asset Allocation</ListItem>
                                    </MenuItem>
                                  )}

                                  <MenuItem
                                    onSelect={() => {
                                      setAccountSelected({ id: account.AccountId });
                                    }}
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <ListItem
                                      icon={
                                        hasAccessToEditProfile ||
                                        (account.IsLinked && canEditLinkedAccountOwnerAndSync) ||
                                        canEditAggregatedLinkedAccountDescriptionOnly(
                                          account,
                                          hasAccessToEditProfile,
                                        )
                                          ? 'fas fa-pencil-alt'
                                          : 'far fa-eye'
                                      }
                                    >
                                      {hasAccessToEditProfile ||
                                      (account.IsLinked && canEditLinkedAccountOwnerAndSync) ||
                                      canEditAggregatedLinkedAccountDescriptionOnly(
                                        account,
                                        hasAccessToEditProfile,
                                      )
                                        ? 'Edit'
                                        : 'View'}
                                    </ListItem>
                                  </MenuItem>

                                  {hasAccessToEditProfile && (
                                    <MenuItem
                                      onSelect={async () => {
                                        try {
                                          await cloneAccount(account);
                                          toast.success('Account cloned successfully.');
                                        } catch (error) {
                                          console.error(error);
                                          toast.error('Unable to clone account. Please try again.');
                                        }
                                      }}
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <ListItem icon="far fa-clone">Clone</ListItem>
                                    </MenuItem>
                                  )}

                                  {hasAccessToLinkAccounts &&
                                    account.IsLinked &&
                                    !account.IsLinkBroken && (
                                      <MenuItem
                                        onSelect={() => {
                                          if (isWorkflow) {
                                            const { params } = ADV.util.Router.getObjFromHash(
                                              ADV.util.getHash(),
                                            );

                                            ADV.util.Router.go2('Workflow', {
                                              ...params,
                                              clientId,
                                              pageId: WorkflowTabSlugOptions.manageLinks,
                                              institutionId: account.ExternalInstitutionLinkId,
                                            });
                                          } else {
                                            ADV.util.Router.go2('ProfileAccounts', {
                                              clientId,
                                              tabSlug: TabSlugOptions.manageLinks,
                                              entityId: account.ExternalInstitutionLinkId,
                                            });
                                          }
                                        }}
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <ListItem icon="far fa-link">View Link</ListItem>
                                      </MenuItem>
                                    )}

                                  {!account.IsLinked && hasAccessToEditProfile && (
                                    <>
                                      <MenuSeparator />

                                      <MenuItem
                                        onSelect={() => {
                                          setAccountToDelete({
                                            accountId: account.AccountId,
                                            accountName: account.Description || account.AccountName,
                                            clientId,
                                          });
                                        }}
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <ListItem icon="far fa-trash-alt" variant="error">
                                          Delete
                                        </ListItem>
                                      </MenuItem>
                                    </>
                                  )}
                                </MenuContent>
                              </Menu>
                            </Box>
                          </td>
                        </ClickableTableRow>
                      );
                    })}
                  </tbody>
                </table>
              </HtmlTable>
            );
          })}
        </>
      )}

      {accountToDelete && (
        <DeleteModal
          {...accountToDelete}
          onSuccess={() => {
            refetch();

            queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY, MANAGE_LINKS_QUERY_KEY]);
          }}
          closeModal={() => {
            setAccountToDelete(null);
          }}
        />
      )}

      {isAddAccountPreSelectionModalOpen && (
        <AddAccountPreSelectionModal
          showExternalProviders
          onManualClick={() => {
            setIsAddAccountPreSelectionModalOpen(false);
            setAccountSelected({ id: '' });
          }}
          onClose={() => {
            setIsAddAccountPreSelectionModalOpen(false);
          }}
          showQuovoMessage={() => setShowQuovoMessage(true)}
          onOpenAkoyaLinkManagementModal={() => {
            setIsAddAccountPreSelectionModalOpen(false);
            setIsAkoyaLinkManagementModalOpen(true);
          }}
        />
      )}

      {accountSelected !== null && (
        <AccountForm
          accountId={accountSelected.id}
          tabSelected={accountSelected.tabSelected}
          onDeleteAccount={setAccountToDelete}
          onClose={() => {
            setAccountSelected(null);
          }}
        />
      )}

      {showQuovoMessage && (
        <QuovoDisabledMessageModal onRequestClose={() => setShowQuovoMessage(false)} />
      )}

      {isAkoyaLinkManagementModalOpen && (
        <ManageAkoyaLinkedAccounts
          onClose={() => {
            setIsAkoyaLinkManagementModalOpen(false);
          }}
          onBack={() => {
            setIsAddAccountPreSelectionModalOpen(true);

            setIsAkoyaLinkManagementModalOpen(false);
          }}
          onExternalAccountLinked={() => {
            setIsAkoyaLinkManagementModalOpen(false);

            setIsAddAccountPreSelectionModalOpen(false);
          }}
        />
      )}
    </>
  );
};
